import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

const ICEBREAKERS = [
  "I need a gift for a minimalist friend",
  "Show me warm neutrals for winter",
  "What's your best home lighting?",
  "Wireless headphones under $500",
];

export default function AIAssistant() {
  const { assistantOpen, setAssistantOpen } = useApp();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: "user", content: text };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat/message", { session_id: sessionId, message: text });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, recommended: data.recommended || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "I'm having trouble connecting. Please try again.", recommended: [] }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setAssistantOpen(true)}
        className="fixed bottom-6 right-6 z-40 group flex items-center gap-3 bg-black text-white pl-4 pr-5 py-3 hover:bg-[#FF3B30] transition-colors ai-glow"
        data-testid="ai-chat-launcher"
      >
        <div className="relative">
          <Sparkles className="w-4 h-4" />
          <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-[#FF3B30] rounded-full group-hover:bg-white animate-pulse" />
        </div>
        <span className="font-mono-brand text-xs uppercase tracking-[0.2em]">Ask Nova</span>
      </button>

      <AnimatePresence>
        {assistantOpen && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-6 right-6 z-40 w-[92vw] max-w-md h-[560px] max-h-[80vh] bg-white/95 backdrop-blur-2xl border border-black/10 flex flex-col shadow-2xl"
            data-testid="ai-chat-panel"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-black/10">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#FF3B30] rounded-full animate-pulse" />
                <div>
                  <div className="font-display font-black text-base leading-none">Nova</div>
                  <div className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-neutral-500">Personal concierge</div>
                </div>
              </div>
              <button onClick={() => setAssistantOpen(false)} data-testid="ai-close-btn" className="hover:text-[#FF3B30]">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
              {messages.length === 0 && (
                <div className="space-y-4">
                  <p className="font-display text-2xl leading-tight tracking-tight">
                    Hi — I curate Nova's catalog. <span className="text-neutral-500">What are you hunting for?</span>
                  </p>
                  <div className="space-y-2">
                    {ICEBREAKERS.map((t, i) => (
                      <button
                        key={i}
                        onClick={() => send(t)}
                        className="block w-full text-left font-mono-brand text-xs uppercase tracking-[0.2em] px-3 py-2 border border-black/10 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
                        data-testid={`icebreaker-${i}`}
                      >
                        → {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
                  <div className={m.role === "user"
                    ? "bg-black text-white px-4 py-2.5 max-w-[85%] text-sm"
                    : "text-sm leading-relaxed max-w-[95%]"}>
                    {m.content}
                    {m.recommended?.length > 0 && (
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        {m.recommended.slice(0, 4).map((p) => (
                          <Link key={p.id} to={`/product/${p.id}`} onClick={() => setAssistantOpen(false)} className="block group">
                            <div className="aspect-square overflow-hidden bg-neutral-100">
                              <img src={p.image + "?auto=format&fit=crop&w=300&q=80"} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                            </div>
                            <div className="font-display text-xs mt-1 leading-tight group-hover:text-[#FF3B30]">{p.name}</div>
                            <div className="font-mono-brand text-[10px] text-neutral-500">${p.price}</div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex items-center gap-2 text-sm text-neutral-500">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span className="font-mono-brand text-[10px] uppercase tracking-widest">Thinking</span>
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(input); }}
              className="border-t border-black/10 p-3 flex gap-2"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about styles, fits, gifts…"
                className="flex-1 px-3 py-2 bg-transparent border-b border-black/10 focus:border-black outline-none text-sm"
                data-testid="ai-chat-input"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-black text-white px-3 hover:bg-[#FF3B30] disabled:opacity-40 transition-colors"
                data-testid="ai-chat-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
